import { useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@sw/api-client';
import { AUTH_ME_QUERY_KEY } from '@sw/auth';
import { api } from '../../lib/api-client';
import { GoogleSignInButton } from './GoogleSignInButton';
import { MicrosoftSignInButton } from './MicrosoftSignInButton';
import { DomainHintPill } from './DomainHintPill';
import { useDomainCheck } from './useDomainCheck';

/**
 * Form de login con 4 métodos visibles: Google OAuth, Microsoft OAuth, password,
 * y magic link. State machine local (modo password vs magic link) controlado por
 * `mode`. Story SW-310.
 *
 * SW-132 bookmark UX: password mode funciona SIN `accessLinkToken` — el backend
 * resuelve el client via tenant (subdomain) + email match. Google / Microsoft /
 * magic link aún requieren `accessLinkToken` porque sus flows (GIS / MSAL / email
 * delivery) necesitan saber el cliente upfront.
 *
 * Anti-enumeration: errores 401 / 403 / 404 se mapean al mismo mensaje genérico
 * "Correo o contraseña incorrectos" para no leakear qué emails / clientes existen.
 */
export function SignInForm({
  accessLinkToken,
  onMagicLinkSent,
  onBlocked,
  onAuthenticated,
}: {
  accessLinkToken: string | null;
  onMagicLinkSent: (email: string) => void;
  onBlocked: (email: string, domain: string) => void;
  onAuthenticated: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'password' | 'magic-link'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // SW-128 opt A: live domain check (debounced 350ms contra /api/client/auth/check-domain).
  // Solo activo cuando hay accessLinkToken — el hook hace skip si el token es null.
  const domainCheck = useDomainCheck(email, accessLinkToken);

  // SW-132: password mode siempre habilitado. Magic link / OAuth requieren token.
  const oauthDisabled = !accessLinkToken;
  const magicLinkRequiresToken = !accessLinkToken;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);

    if (!email.trim()) {
      setFormError(t('login.errors.emailRequired'));
      return;
    }
    if (mode === 'password' && !password) {
      setFormError(t('login.errors.passwordRequired'));
      return;
    }
    if (mode === 'magic-link' && magicLinkRequiresToken) {
      setFormError(t('login.errors.missingAccessLink'));
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'password') {
        // SW-132: accessLinkToken opcional. Si no está, el backend resuelve via
        // subdomain (tenant) + email match en el workspace.
        await api.post('/api/client/auth/password/login', {
          email: email.trim(),
          password,
          ...(accessLinkToken ? { accessLinkToken } : {}),
        });
        // Auth cookie ya seteada — invalida /me para que useAuth refleje el nuevo state.
        await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
        onAuthenticated();
      } else {
        // Magic link: backend siempre devuelve 200 con mensaje genérico (anti-enum).
        await api.post('/api/client/auth/magic-link/request', {
          email: email.trim(),
          accessLinkToken,
        });
        onMagicLinkSent(email.trim());
      }
    } catch (err) {
      handleError(err);
    } finally {
      setSubmitting(false);
    }
  }

  function handleError(err: unknown) {
    if (!(err instanceof ApiError)) {
      setFormError(t('login.errors.unexpected'));
      return;
    }
    if (err.status === 429) {
      setFormError(t('login.errors.rateLimited'));
      return;
    }
    if (err.status === 403) {
      // Blocked domain — surface a BlockedState con el dominio extraído del email.
      const domain = email.split('@').pop() ?? '';
      onBlocked(email.trim(), domain);
      return;
    }
    // 400 / 401 / 404 / 5xx: mensaje genérico anti-enum.
    setFormError(
      mode === 'password' ? t('login.errors.invalidCredentials') : t('login.errors.unexpected'),
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} data-testid="login-signin-form" noValidate>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t('login.title')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3
        }}>
        {t('login.lede')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <GoogleSignInButton
          accessLinkToken={accessLinkToken}
          disabled={oauthDisabled || submitting}
          onBlocked={onBlocked}
          onAuthenticated={onAuthenticated}
        />
        <MicrosoftSignInButton
          accessLinkToken={accessLinkToken}
          disabled={oauthDisabled || submitting}
          onBlocked={onBlocked}
          onAuthenticated={onAuthenticated}
        />
      </Box>
      <Divider sx={{ my: 2, fontSize: 12, color: 'text.secondary' }}>{t('login.or')}</Divider>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          id="login-email"
          name="email"
          type="email"
          label={t('login.workEmail')}
          placeholder={t('login.workEmailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          fullWidth
          required
          disabled={submitting}
          slotProps={{
            htmlInput: { 'data-testid': 'login-email-input' }
          }}
        />

        <DomainHintPill status={domainCheck.status} domain={domainCheck.domain} />

        {mode === 'password' && (
          <TextField
            id="login-password"
            name="password"
            type="password"
            label={t('login.password')}
            placeholder={t('login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            fullWidth
            required
            disabled={submitting}
            slotProps={{
              htmlInput: { 'data-testid': 'login-password-input' }
            }}
          />
        )}

        {formError && (
          <Alert severity="error" data-testid="login-form-error">
            {formError}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={submitting || (mode === 'magic-link' && magicLinkRequiresToken)}
          data-testid="login-submit"
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {submitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              {mode === 'password' ? t('login.signingIn') : t('login.sendingMagicLink')}
            </Box>
          ) : mode === 'password' ? (
            t('login.signIn')
          ) : (
            t('login.sendMagicLink')
          )}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 0.5 }}>
          <MuiLink
            component="button"
            type="button"
            onClick={() => {
              setFormError(null);
              setPassword('');
              setMode(mode === 'password' ? 'magic-link' : 'password');
            }}
            disabled={submitting}
            sx={{ fontSize: 13, textAlign: 'center' }}
            data-testid="login-toggle-mode"
          >
            {mode === 'password' ? t('login.useMagicLink') : t('login.usePassword')}
          </MuiLink>
        </Box>
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          display: 'block',
          mt: 3,
          textAlign: 'center'
        }}>
        {t('login.helper')}
      </Typography>
    </Box>
  );
}
